/**
 * Chamadas da API de violações (espelha as funções de violações do `client/api.py`
 * e o `services/violations.py` do painel antigo).
 */
import { apiFetchTexto } from '@/lib/api'

import { type RegraCatalogo, validarCatalogoRegras } from '@/api/regras'
import { lerJsonPython, NumeroPython } from '@/api/violacoes-json'
import { buscarValidado } from '@/api/violacoes-validacao'

// ── Tipos (client/schemas.py e shared/schemas.py) ─────────────────────────

/** Linha de violação (`GET /violations`). */
export interface Violacao {
  id: number
  gitlab_project_id: number
  project_path_with_namespace: string | null
  project_name: string | null
  gitlab_group_id: number | null
  resource_type: string
  resource_iid: number
  rule_catalog_id: number
  rule_code: string
  author_username: string
  detected_at: string
}

export interface ContagemPorRegra {
  rule_code: string
  rule_name: string
  rule_category: string
  count: number
}

export interface ContagemPorCategoria {
  category: string
  count: number
}

export interface ContagemPorProjeto {
  project_path_with_namespace: string | null
  project_name: string | null
  count: number
}

export type Granularidade = 'day' | 'week'

export interface SerieViolacoes {
  granularity: Granularidade
  series: { period: string; count: number }[]
}

export interface SerieEventosWebhook {
  granularity: Granularidade
  series: {
    period: string
    events_count: number
    events_with_violations: number
    events_without_violations: number
  }[]
}

/** Projeto ou grupo com violações no intervalo (`ScopeSearchItem`). */
export interface ItemEscopo {
  entity_type: 'project' | 'group'
  full_path: string
  name: string
  parent_group_path: string | null
  gitlab_project_id: number | null
  count: number
}

// ── Parâmetros ────────────────────────────────────────────────────────────

/** Escopo das rotas de violação (projeto ou prefixo de grupo). */
export interface EscopoApi {
  gitlab_project_id: number | null
  gitlab_group_id: number | null
  namespace_path_prefix: string | null
}

/** Filtros comuns: escopo, autor e intervalo (datas em `AAAA-MM-DD`). */
export interface FiltrosViolacoes extends Partial<EscopoApi> {
  author_username?: string | null
  since?: string | null
  until?: string | null
}

type Params = Record<string, string | number>

/** Mesma montagem do `_violations_query_params` (extras primeiro, vazios omitidos). */
function paramsViolacoes(f: FiltrosViolacoes, extra: Params = {}): Params {
  const params: Params = { ...extra }
  if (f.gitlab_project_id != null) params.gitlab_project_id = f.gitlab_project_id
  if (f.gitlab_group_id != null) params.gitlab_group_id = f.gitlab_group_id
  if (f.namespace_path_prefix != null && f.namespace_path_prefix.trim()) {
    params.namespace_path_prefix = f.namespace_path_prefix.trim()
  }
  if (f.author_username != null && f.author_username.trim()) {
    params.author_username = f.author_username.trim()
  }
  if (f.since != null) params.since = f.since
  if (f.until != null) params.until = f.until
  return params
}

// ── Chamadas ──────────────────────────────────────────────────────────────

export function listarViolacoes(f: FiltrosViolacoes, limit = 500, offset = 0): Promise<Violacao[]> {
  return buscarValidado('violacoes', '/violations', {
    params: paramsViolacoes(f, { limit, offset }),
  })
}

export function resumoPorRegra(f: FiltrosViolacoes): Promise<ContagemPorRegra[]> {
  return buscarValidado('porRegra', '/violations/summary/by-rule', { params: paramsViolacoes(f) })
}

export function resumoPorCategoria(f: FiltrosViolacoes): Promise<ContagemPorCategoria[]> {
  return buscarValidado('porCategoria', '/violations/summary/by-category', {
    params: paramsViolacoes(f),
  })
}

export function resumoPorProjeto(f: FiltrosViolacoes, top_n = 20): Promise<ContagemPorProjeto[]> {
  return buscarValidado('porProjeto', '/violations/summary/by-project', {
    params: paramsViolacoes(f, { top_n }),
  })
}

export function serieTemporalViolacoes(
  granularity: Granularidade,
  f: FiltrosViolacoes
): Promise<SerieViolacoes> {
  return buscarValidado('serie', '/violations/summary/timeseries', {
    params: paramsViolacoes(f, { granularity }),
  })
}

/** Eventos de webhook processados vs com violação, por período. */
export function serieTemporalEventosWebhook(
  granularity: Granularidade,
  f: FiltrosViolacoes
): Promise<SerieEventosWebhook> {
  return buscarValidado('serieWebhooks', '/webhook-events/summary/timeseries', {
    params: paramsViolacoes(f, { granularity }),
  })
}

/** Escopos (projeto/grupo) derivados das violações no intervalo (`fetch_scope_options`). */
export function buscarEscopos(opcoes: {
  q?: string | null
  limit?: number
  since?: string | null
  until?: string | null
  author_username?: string | null
}): Promise<ItemEscopo[]> {
  const params: Params = { limit: opcoes.limit ?? 200 }
  if (opcoes.q != null && opcoes.q.trim()) params.q = opcoes.q.trim()
  if (opcoes.since != null) params.since = opcoes.since
  if (opcoes.until != null) params.until = opcoes.until
  if (opcoes.author_username != null) params.author_username = opcoes.author_username
  return buscarValidado('escopos', '/violations/search/scopes', { params })
}

// ── Utilitários (services/violations.py) ──────────────────────────────────

export function escopoVazio(): EscopoApi {
  return { gitlab_project_id: null, gitlab_group_id: null, namespace_path_prefix: null }
}

/** Projeto/grupo → parâmetros de escopo das APIs. */
export function escopoDoItem(item: ItemEscopo): EscopoApi {
  if (item.entity_type === 'project') {
    return {
      gitlab_project_id: item.gitlab_project_id,
      gitlab_group_id: null,
      namespace_path_prefix: null,
    }
  }
  return { gitlab_project_id: null, gitlab_group_id: null, namespace_path_prefix: item.full_path }
}

export function rotuloEscopo(item: ItemEscopo): string {
  return item.entity_type === 'project'
    ? `Projeto — ${item.full_path}`
    : `Grupo — ${item.full_path}`
}

/** Dias entre duas datas `AAAA-MM-DD`. */
function diasEntre(inicio: string, fim: string): number {
  return Math.round(
    (Date.parse(`${fim}T00:00:00Z`) - Date.parse(`${inicio}T00:00:00Z`)) / 86_400_000
  )
}

/** Dia se a janela tiver até 60 dias, senão semana. */
export function escolherGranularidade(
  since: string,
  until: string | null,
  hoje: string
): Granularidade {
  const fim = until ?? hoje
  return diasEntre(since, fim) + 1 <= 60 ? 'day' : 'week'
}

/** Soma de eventos com e sem violação na série de webhooks. */
export function totaisComSemViolacao(serie: SerieEventosWebhook | null): [number, number] {
  if (!serie) return [0, 0]
  let com = 0
  let sem = 0
  for (const p of serie.series) {
    com += p.events_with_violations
    sem += p.events_without_violations
  }
  return [com, sem]
}

/** Contagem por regra, da maior para a menor (empate: ordem de aparição). */
export function contarPorRegra(violacoes: Violacao[]): Map<string, number> {
  const contagem = new Map<string, number>()
  for (const v of violacoes) contagem.set(v.rule_code, (contagem.get(v.rule_code) ?? 0) + 1)
  return new Map([...contagem.entries()].sort((a, b) => b[1] - a[1]))
}

/** Data de hoje (fuso do navegador) em `AAAA-MM-DD`. */
export function hojeIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Início padrão da janela: 30 dias antes de hoje. */
export function inicioPadraoIso(): string {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// ── Busca de autores e heatmap autor × regra (página "Violações") ─────────

/** Autor distinto com contagem de violações (`ViolationAuthorSearchItem`). */
export interface ItemAutor {
  author_username: string
  count: number
}

/** Contagem por autor e regra (`ViolationCountByAuthorRule`). */
export interface ContagemPorAutorRegra {
  author_username: string
  rule_code: string
  count: number
}

/** Autores distintos no intervalo, opcionalmente restritos a projeto/grupo (`fetch_author_options`). */
export function buscarAutores(
  opcoes: FiltrosViolacoes & { q?: string | null; limit?: number }
): Promise<ItemAutor[]> {
  const params = paramsViolacoes(
    { ...opcoes, author_username: null },
    { limit: opcoes.limit ?? 200 }
  )
  if (opcoes.q != null && opcoes.q.trim()) params.q = opcoes.q.trim()
  return buscarValidado('autores', '/violations/search/authors', { params })
}

/** Heatmap autor × regra (`/violations/summary/by-author-rule`). */
export function resumoPorAutorRegra(
  f: FiltrosViolacoes,
  top_authors = 15,
  top_rules = 20
): Promise<ContagemPorAutorRegra[]> {
  return buscarValidado('porAutorRegra', '/violations/summary/by-author-rule', {
    params: paramsViolacoes(f, { top_authors, top_rules }),
  })
}

/** Troca os números especiais do leitor de JSON do Python pelo número comum do JavaScript. */
function numerosComuns(valor: unknown): unknown {
  if (valor instanceof NumeroPython) return valor.valor
  if (Array.isArray(valor)) return valor.map(numerosComuns)
  if (typeof valor === 'object' && valor !== null) {
    return Object.fromEntries(
      Object.entries(valor as Record<string, unknown>).map(([k, v]) => [k, numerosComuns(v)])
    )
  }
  return valor
}

/**
 * `GET /rules` para o catálogo da página: lê o JSON como o `json.loads` do Python (mesma
 * mensagem e posição quando a resposta vem quebrada) e confere como `list[RuleCatalogResponse]`.
 */
export async function listarRegrasCatalogo(): Promise<RegraCatalogo[]> {
  const corpo = lerJsonPython(await apiFetchTexto('/rules'))
  return validarCatalogoRegras(numerosComuns(corpo))
}
